import { createFileRoute } from "@tanstack/react-router";
import { useRef } from "react";
import { ChevronDown } from "lucide-react";

import ranchHeritage from "@/assets/ranch-heritage.jpg";
import ranchGate from "@/assets/heritage-ranch-gate-1990.jpg";
import ranchAerial from "@/assets/heritage-ranch-aerial.jpg";
import rbFeather from "@/assets/heritage-rb-feather.png";

function TapedPhoto({
  src,
  alt,
  caption,
  rotation = "-1.5deg",
}: {
  src: string;
  alt: string;
  caption?: string;
  rotation?: string;
}) {
  return (
    <div className="relative mx-auto w-full max-w-[480px] pt-8">
      <div
        className="relative transform-gpu shadow-[0_18px_40px_-18px_rgba(65,32,25,0.45)]"
        style={{ transform: `rotate(${rotation})` }}
      >
        {/* Photo card (paper feel) */}
        <div className="bg-[#FDFAF3] p-3 pb-8">
          <div className="aspect-square w-full overflow-hidden bg-muted">
            <img
              src={src}
              alt={alt}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </div>
          {caption && (
            <p className="mt-4 px-1 text-center font-ui text-[10px] font-bold uppercase tracking-[0.3em] text-[#2A1510]/90">
              {caption}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/heritage")({
  head: () => ({
    meta: [
      { title: "Heritage | Coyote Creek Hat Co." },
      { name: "description", content: "Founded in 2023, Coyote Creek Hat Co. is a premium Texas sporting brand built for the field, the ranch, and everywhere in between." },
      { property: "og:title", content: "Heritage | Coyote Creek Hat Co." },
      { property: "og:description", content: "Our story begins in the heart of Texas. Heritage, mission, and values." },
      { property: "og:url", content: "https://coyotecreekhatco.com/heritage" },
      { property: "og:image", content: "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/Memories.png?v=1764132472" },
      { name: "twitter:image", content: "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/Memories.png?v=1764132472" },
    ],
    links: [{ rel: "canonical", href: "https://coyotecreekhatco.com/heritage" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "Heritage | Coyote Creek Hat Co.",
        url: "https://coyotecreekhatco.com/heritage",
        description: "Founded in 2023, Coyote Creek Hat Co. is a premium Texas sporting brand built for the field, the ranch, and everywhere in between.",
        mainEntity: {
          "@type": "Organization",
          name: "Coyote Creek Hat Co.",
          foundingDate: "2023",
          foundingLocation: { "@type": "Place", address: { "@type": "PostalAddress", addressRegion: "TX", addressCountry: "US" } },
          url: "https://coyotecreekhatco.com",
        },
      }),
    }],
  }),
  component: HeritagePage,
});

function HeritagePage() {
  const letterRef = useRef<HTMLElement>(null);

  const scrollToLetter = () => {
    const el = letterRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const target = window.scrollY + rect.top + rect.height / 2 - window.innerHeight / 2;
    window.scrollTo({ top: target, behavior: "smooth" });
  };

  return (
    <div>
      {/* Hero */}
      <div className="relative w-full h-[50vh] min-h-[320px] overflow-hidden">
        <img src="https://cdn.shopify.com/s/files/1/0808/1332/6620/files/Memories.png?v=1764132472" alt="Coyote Creek heritage" className="absolute inset-0 h-full w-full object-cover" width={1920} height={768} />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <h1 className="font-heading text-5xl md:text-7xl uppercase tracking-[0.15em] text-white text-center px-6 drop-shadow-lg">
            Heritage
          </h1>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <button
            type="button"
            onClick={scrollToLetter}
            aria-label="Scroll to founder's letter"
            className="flex h-12 w-12 items-center justify-center rounded-full bg-blaze animate-bounce transition-transform hover:scale-110"
          >
            <ChevronDown className="h-6 w-6 text-[#F5EFE2]" />
          </button>
        </div>
      </div>

      {/* Founder Letter */}
      <section ref={letterRef} className="py-20 md:py-28">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <span className="font-ui text-xs font-bold uppercase tracking-[0.3em] text-[#C6A971]">Howdy,</span>
          <div className="mt-8 space-y-6 font-body text-base md:text-lg leading-relaxed text-muted-foreground text-left">
            <p>
              Coyote Creek was founded in 2023 during my sophomore year at Rice University. What began as a passion project rooted in faith, family, and the outdoors quickly grew into something much bigger than I ever imagined.
            </p>
            <p>
              Growing up with two brothers, we thrived in the outdoors — playing sports, hunting. golfing, and chasing the next adventure. Those early mornings on the field and long days outside shaped who we are today and inspired the brand you see now.
            </p>
            <p>
              We are a brand built on faith, family, and the sporting life. Every hat we make is a reflection of the lifestyle we love and the heritage we are proud to carry forward. They belong everywhere your journey begins.
            </p>
            <p>
              Thank y'all for being part of our journey. God bless!
            </p>
          </div>
          <p className="mt-10 text-right font-heading italic text-2xl text-primary">— Graiden</p>
        </div>
      </section>

      {/* 3-up image strip */}
      <section className="grid grid-cols-1 md:grid-cols-3">
        <img src="https://cdn.shopify.com/s/files/1/0808/1332/6620/files/72073502393__7E5DC768-9C66-41E8-9022-BEAB56C8856E.jpg?v=1699984696" alt="Coyote Creek Ranch heritage" className="w-full aspect-[4/3] object-cover" />
        <img src="https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG_9396.jpg?v=1706758562" alt="Coyote Creek cowboy hat" className="w-full aspect-[4/3] object-cover" />
        <img src={ranchAerial} alt="Aerial view of Coyote Creek Ranch" className="w-full aspect-[4/3] object-cover" />
      </section>

      {/* Our Mission */}
      <section className="bg-card py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <span className="font-ui text-xs font-bold uppercase tracking-[0.3em] text-[#C6A971]">Our Mission</span>
          <p className="mt-8 font-heading text-3xl md:text-5xl uppercase leading-[1.1] text-primary italic">
            CREATE A COMMUNITY AROUND HATS BUILT ON YOUR LIFESTYLE.
          </p>
        </div>
      </section>

      {/* Story 1 — Coyote Creek Ranch */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <TapedPhoto
              src={ranchGate}
              alt="Coyote Creek Ranch gate"
              caption="Coyote Creek Ranch — New Ulm, Texas"
              rotation="-1.5deg"
            />
            <div className="space-y-5">
              <span className="font-ui text-xs font-bold uppercase tracking-[0.3em] text-[#C6A971]">Heritage</span>
              <h2 className="font-heading text-3xl md:text-4xl uppercase text-primary">Coyote Creek Ranch</h2>
              <p className="font-body text-base md:text-lg leading-relaxed text-muted-foreground">
                The origin story of Coyote Creek Hat Co. lies within the historic Coyote Creek Ranch. Located in the heart of New Ulm, Texas, the ranch became family owned and maintained — a place where generations have worked the land, raised animals, and passed down a way of life that's never gone out of style.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Story 2 — Barnett Quarter Horses (image right) */}
      <section className="py-20 md:py-28 bg-card">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-5 md:order-1">
              <span className="font-ui text-xs font-bold uppercase tracking-[0.3em] text-[#C6A971]">Barnett Quarter Horses</span>
              <h2 className="font-heading text-3xl md:text-4xl uppercase text-primary">Overview</h2>
              <p className="font-body text-base md:text-lg leading-relaxed text-muted-foreground">
                Established in 1992, Coyote Creek Ranch was home to Barnett Quarter Horses — a program built on a love for cutting horses, hard-earned belt buckles, and the kind of horsemanship you can only learn through long days in the saddle. That same dedication to craft and tradition is stitched into every hat we make today.
              </p>
            </div>
            <div className="md:order-2">
              <TapedPhoto
                src={ranchAerial}
                alt="Aerial view of Barnett Quarter Horses ranch"
                caption="Barnett Quarter Horses — Est. 1992"
                rotation="1.5deg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Story 3 — The Outdoors (image left) */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <TapedPhoto
              src={rbFeather}
              alt="Coyote Creek hat with RB feather pin"
              caption="From the field to the fairway"
              rotation="-1.5deg"
            />
            <div className="space-y-5">
              <span className="font-ui text-xs font-bold uppercase tracking-[0.3em] text-[#C6A971]">Carryin' On The Legacy</span>
              <h2 className="font-heading text-3xl md:text-4xl uppercase text-primary">The Outdoors</h2>
              <p className="font-body text-base md:text-lg leading-relaxed text-muted-foreground">
                The Outdoors just simply runs in our lifestyle. From sunrise duck hunts to late-afternoon fishing trips, from family deer leases to weekends on the ranch — it's the thread that ties every Coyote Creek story together. We build for the people who live it. God bless Texas and God bless the USA.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 md:py-28 bg-card">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center">
            <span className="font-ui text-xs font-bold uppercase tracking-[0.3em] text-[#C6A971]">What We Stand For</span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl uppercase text-primary">Core Values</h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              { title: "Faith & Tradition", desc: "Grounded in strong, enduring principles and a deep respect for American heritage." },
              { title: "The Sporting Spirit", desc: "Celebrating the drive, camaraderie, and tradition of sportsmanship, from the duck blind to the golf course." },
              { title: "Uncompromising Craftsmanship", desc: "Designing premium, durable goods that look as good on the weekend as they do in the backcountry." },
              { title: "Authenticity", desc: "Remaining true to our roots—approachable, genuine, and unapologetically bold." },
            ].map(v => (
              <div key={v.title} className="rounded-xl border border-border bg-background p-6">
                <h3 className="font-heading text-xl text-primary">{v.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Promise */}
      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <span className="font-ui text-xs font-bold uppercase tracking-[0.3em] text-primary/60">
            Our Brand Promise
          </span>
          
          <p className="mt-10 font-heading text-3xl italic leading-[1.15] tracking-tight text-primary sm:text-4xl md:text-5xl">
            "Just like we take pride in our heritage, we take pride in our quality."
          </p>
          <div className="mx-auto mt-10 h-px w-20 bg-primary/30" />
          <p className="mt-6 font-ui text-xs font-bold uppercase tracking-[0.3em] text-primary/70">
            BUILT FOR THE SPORTING LIFE.
          </p>
        </div>
      </section>
    </div>
  );
}
